import { useState, useEffect, useRef, useCallback } from 'react';
import { Play } from 'lucide-react';
import type { WorkflowPriority } from '../../../types/workflow-v9';
import type { ExecutionTemplateSummary } from '../../../services/executionService';

interface ExecutionCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: ExecutionTemplateSummary[];
  onSubmit: (payload: { templateId?: string; blueprintId?: string; versionId?: string; title: string; priority: WorkflowPriority; notes?: string }) => Promise<void>;
  isSubmitting: boolean;
}

const PRIORITY_OPTIONS: Array<{ value: WorkflowPriority; label: string; color: string; activeColor: string }> = [
  { value: 'baixa',   label: 'Baixa',   color: 'border-slate-200 dark:border-slate-700', activeColor: 'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400 dark:border-green-600' },
  { value: 'media',   label: 'Média',   color: 'border-slate-200 dark:border-slate-700', activeColor: 'border-yellow-400 bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-400 dark:border-yellow-600' },
  { value: 'alta',    label: 'Alta',     color: 'border-slate-200 dark:border-slate-700', activeColor: 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-600' },
  { value: 'critica', label: 'Crítica',  color: 'border-slate-200 dark:border-slate-700', activeColor: 'border-red-400 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400 dark:border-red-600' },
];

function formatDateShort(): string {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function ExecutionCreateModal({ isOpen, onClose, templates, onSubmit, isSubmitting }: ExecutionCreateModalProps) {
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<WorkflowPriority>('media');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  const selectedTemplate = templates.find(t => t.id === templateId);

  useEffect(() => {
    if (isOpen) {
      const firstTemplate = templates[0];
      const tId = firstTemplate?.id ?? '';
      setTemplateId(tId);
      const name = firstTemplate?.name ?? 'Execução';
      setTitle(`${name} - ${formatDateShort()}`);
      setPriority('media');
      setNotes('');
      setError(null);
      setTimeout(() => titleRef.current?.select(), 50);
    }
  }, [isOpen, templates]);

  const handleTemplateChange = useCallback((newId: string) => {
    setTemplateId(newId);
    const tpl = templates.find(t => t.id === newId);
    if (tpl) {
      setTitle(`${tpl.name} - ${formatDateShort()}`);
    }
  }, [templates]);

  const handleSubmit = useCallback(async () => {
    if (!templateId) {
      setError('Selecione um processo');
      return;
    }
    if (!title.trim()) {
      setError('Informe um título');
      return;
    }

    setError(null);
    try {
      if (selectedTemplate?.latestVersionId) {
        await onSubmit({
          blueprintId: templateId,
          versionId: selectedTemplate.latestVersionId,
          title: title.trim(),
          priority,
          notes: notes.trim() || undefined,
        });
      } else {
        await onSubmit({
          templateId,
          title: title.trim(),
          priority,
          notes: notes.trim() || undefined,
        });
      }
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'Não foi possível criar a execução.';
      setError(message);
    }
  }, [templateId, title, priority, notes, selectedTemplate, onSubmit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  }, [handleSubmit, isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-[var(--bg-surface)]"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">Nova execução</p>
          <h2 className="mt-0.5 text-[15px] font-semibold text-slate-900 dark:text-slate-50">
            {selectedTemplate?.name ?? 'Selecione um processo'}
          </h2>
          {selectedTemplate?.category && (
            <span className="mt-0.5 inline-block text-[11px] text-slate-500 dark:text-slate-400">
              {selectedTemplate.category}
              {selectedTemplate.versionSemver && ` · v${selectedTemplate.versionSemver}`}
            </span>
          )}
        </div>

        {/* Form */}
        <div className="space-y-3 px-5 py-4">
          {/* Template selector */}
          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400">Processo</label>
            <select
              value={templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="h-[30px] w-full border border-slate-300 bg-white px-2 text-[12px] text-slate-900 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="" disabled>Selecione um processo</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.versionSemver ? `(v${t.versionSemver})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400">Título da execução *</label>
            <input
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-[30px] w-full border border-slate-300 bg-white px-2 text-[12px] text-slate-900 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          {/* Priority */}
          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400">Prioridade</label>
            <div className="flex items-center gap-1.5">
              {PRIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPriority(opt.value)}
                  className={`flex-1 border px-2 py-1 text-[11px] font-medium transition-colors ${
                    priority === opt.value
                      ? opt.activeColor
                      : `${opt.color} bg-white text-slate-500 hover:border-slate-400 dark:bg-slate-900 dark:text-slate-400`
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400">Observação inicial</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Opcional — contexto ou instruções para esta execução"
              className="w-full resize-none border border-slate-300 bg-white px-2 py-1.5 text-[12px] text-slate-900 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 placeholder:text-slate-400"
            />
          </div>

          {error && (
            <p className="border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="border border-slate-300 bg-white px-4 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !templateId}
            className="inline-flex items-center gap-1.5 border border-[#4c627b] bg-[#4c627b] px-4 py-1.5 text-[11px] font-medium text-white hover:bg-[#3d5266] disabled:opacity-50"
          >
            <Play size={12} />
            {isSubmitting ? 'Criando…' : 'Iniciar Execução'}
          </button>
        </div>
      </div>
    </div>
  );
}
